import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout';
import { CheckCircle2, Eye, Target, MapPin, Recycle, ShieldCheck } from 'lucide-react';

const SITE_URL = 'https://www.patekarecycle.com';

const missionPoints = [
  'Implement efficient waste collection, segregation, and recycling systems',
  'Build traceable and compliant resource recovery channels',
  'Partner with businesses, communities, and brands for end-to-end waste solutions',
  'Empower local stakeholders and create economic opportunities',
];

const values = [
  {
    icon: ShieldCheck,
    title: 'Transparency',
    description: 'Every material tracked from source to end-use with full documentation.',
  },
  {
    icon: Recycle,
    title: 'Circularity',
    description: 'Operations designed to keep materials in the economy, not landfills.',
  },
  {
    icon: Target,
    title: 'Compliance',
    description: 'Systems built to meet EPR, CPCB, and state-level regulatory requirements.',
  },
  {
    icon: Eye,
    title: 'Impact',
    description: 'Measurable environmental and social outcomes for every client.',
  },
];

const galleryImages = [
  { src: '/images/gallery-04.webp', alt: 'Waste collection' },
  { src: '/images/gallery-08.webp', alt: 'Material sorting' },
  { src: '/images/gallery-11.webp', alt: 'Recycling operations' },
  { src: '/images/gallery-13.webp', alt: 'Resource recovery' },
  { src: '/images/gallery-18.webp', alt: 'Community program' },
  { src: '/images/gallery-20.webp', alt: 'Waste processing' },
];

export function About() {
  return (
    <Layout>
      <Helmet>
        <title>About Punarvaa | Patekar Recycle Solution Pvt Ltd - Waste Recovery India</title>
        <meta name="description" content="Punarvaa by Patekar Recycle Solution Pvt Ltd builds structured systems to recover value from waste. 8+ years of compliant waste management across India." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/about`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:title" content="About Punarvaa | Patekar Recycle Solution Pvt Ltd" />
        <meta property="og:description" content="8+ years of structured waste recovery, recycling, and resource recovery solutions for industries and communities across India." />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Punarvaa | Patekar Recycle Solution Pvt Ltd" />
        <meta name="twitter:description" content="8+ years of structured waste recovery solutions for industries and communities across India." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="text-sm font-semibold text-gray-500 mb-3 tracking-wide">
              Patekar Recycle Solution Pvt Ltd
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-display text-white leading-[0.95] mb-6">
              We build systems that convert waste into resources.
            </h1>
            <p className="text-lg text-gray-400 mb-10">
              8+ years. Pan-India. Structured. Compliant.
            </p>
            <div className="flex items-center gap-0 divide-x divide-gray-700">
              {['8+ Years', 'Pan-India', 'DPIIT Certified'].map((stat) => (
                <div key={stat} className="px-6 first:pl-0">
                  <span className="text-sm font-semibold text-gray-300">{stat}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold font-display text-gray-900 mb-6">
                Patekar Recycle Solution Pvt Ltd
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-p-700">Patekar Recycle Solution Pvt Ltd</strong>, operating
                  under the brand <strong className="text-p-700">Punarvaa</strong>, builds systems
                  that convert waste streams into resource streams.
                </p>
                <p>
                  Industries generate large volumes of recyclable and non-recyclable waste. Without
                  structured handling, this material moves into landfills or informal disposal
                  channels — leading to waste of resource, misuse of scrap material, brand damage,
                  and legal issues.
                </p>
                <p>
                  Punarvaa addresses this gap through organised waste collection, material
                  segregation, verified recycling partnerships, and traceable reporting systems.
                </p>
                <p>
                  Our goal is simple: recover value from waste while protecting the environment and
                  business compliance. With deep industry expertise, structured processes, and strong
                  partnerships, we deliver compliant, transparent, and scalable waste solutions
                  across India.
                </p>
              </div>

              <div className="mt-8 space-y-2">
                {[
                  'Multi-material recovery expertise',
                  'Transparent and traceable waste material tracking',
                  'Compliance-ready systems',
                  'Circular economy focused operations',
                  'ESG reporting support',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-p-600 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Stats — dark editorial, no icon cards */}
              <div className="grid grid-cols-2 gap-px bg-gray-200 mb-8 rounded-xl overflow-hidden">
                {[
                  { value: '8+', label: 'Years Experience' },
                  { value: 'Pan-India', label: 'Operations' },
                  { value: 'Multi-material', label: 'Recovery Focus' },
                  { value: '100%', label: 'Compliance Rate' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-gray-950 p-6">
                    <p className="text-2xl sm:text-3xl font-bold text-white mb-1">{stat.value}</p>
                    <p className="text-xs font-semibold tracking-widest uppercase text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Registered address */}
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3">Registered Address</p>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-p-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-600">
                    Vahuli Village, Vadpe Bhiwandi,<br />
                    Thane, Maharashtra – 421302
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="py-16 bg-gray-950 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-4">Why We Exist</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              India generates tons of waste every day.
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-6">
              Without proper systems, garbage ends up in landfills, water bodies, or open spaces.
              Punarvaa closes the loop — we recover resources, reduce environmental harm, and
              reintroduce value back to the economy.
            </p>
            <div className="flex items-center gap-4 text-p-400 font-semibold text-lg">
              <span>Waste</span>
              <span className="text-gray-600">→</span>
              <span>Resource</span>
              <span className="text-gray-600">→</span>
              <span>Value</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-br from-p-800 to-p-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 bg-p-500/20 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-p-300" />
              </div>
              <h3 className="text-2xl font-bold font-display mb-4">Our Vision</h3>
              <p className="text-p-100 text-lg">
                To create a world where nothing is wasted — where every material has value and
                every community thrives sustainably.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 bg-p-500/20 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-p-300" />
              </div>
              <h3 className="text-2xl font-bold font-display mb-4">Our Mission</h3>
              <ul className="space-y-3 text-p-100">
                {missionPoints.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-p-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-gray-900 mb-4">Our Values</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">The principles that guide everything we do</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => (
              <motion.div
                key={value.title}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                className="bg-p-50 rounded-2xl p-6 text-center hover:bg-p-100 transition-colors"
              >
                <div className="w-14 h-14 bg-p-200 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-p-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-8">Our Operations</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {galleryImages.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="overflow-hidden rounded-xl aspect-video"
              >
                <picture>
                  <source srcSet={img.src} type="image/webp" />
                  <img
                    src={img.src}
                    alt={img.alt}
                    width={600}
                    height={400}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </picture>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Initiatives */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">Community & Circular Initiatives</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Punarvaa promotes resource recovery beyond business operations.
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Glass recovery campaigns',
                'Plastic collection drives',
                'Community awareness programs',
                'Swachh initiatives',
                'Beach cleanup campaigns',
                'Local employment creation',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 p-4 bg-p-50 rounded-xl">
                  <span className="w-2 h-2 rounded-full bg-p-600 shrink-0" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
